import { Injectable, InjectionToken, Provider } from '@angular/core';
import { mockReportDocuments, mockReportDocumentTemplates } from '../../mock-data/report.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import {
	ApiReportDocument,
	ApiReportDocumentTemplate,
	ApiReportDocumentUpdateBody,
} from '../api-types/report-document.api';
import { ReportDocument, ReportDocumentTemplate, ReportDocumentUpdateBody } from '../app-types/report-document.app';
import {
	transformApiReportDocumentTemplatesToReportDocumentTemplates,
	transformApiReportDocumentTemplateToReportDocumentTemplate,
	transformApiReportDocumentToReportDocument,
	transformReportUpdateBodyToApiReportUpdateBody,
} from '../transformers/report.transform';
import { BaseRequestService } from './base-request.service';

export interface ReportProvider {
	prefillTemplateForAssignment: (
		assignmentId: string | number,
		templateId: string | number,
	) => Promise<ReportDocumentTemplate>;
	getDocumentTemplates: (
		typeOfCareId?: number | string,
		offset?: number,
		range?: number,
		forAdditionalAssignment?: boolean,
	) => Promise<PaginatedResponse<ReportDocumentTemplate>>;
	createReportDocument: (reportDocumentTemplateId: string | number, assignmentId: string | number) => Promise<number>;
	updateReportDocument: (data: ReportDocumentUpdateBody) => Promise<void>;
	getDocumentsOfAssignment: (
		assignmentId: string | number,
		offset?: number,
		range?: number,
	) => Promise<PaginatedResponse<ReportDocument>>;
	getReportOfAssignment: (assignmentId: string | number, reportId: string | number) => Promise<ReportDocument>;
	getPdfOfReportDocument: (assignmentId: string | number, reportId: string | number) => Promise<Blob>;
	updateReportTemplate: (templateId: string | number, htmlString: string) => Promise<void>;
}

export const REPORT_API = new InjectionToken<ReportProvider>('REPORT_API');

@Injectable()
export class ReportApi extends BaseRequestService implements ReportProvider {
	public async prefillTemplateForAssignment(
		assignmentId: string | number,
		templateId: string | number,
	): Promise<ReportDocumentTemplate> {
		const response = await this.get<ApiReportDocumentTemplate>(`assignments/reportdocuments/templates/${templateId}`, {
			prefillTemplate: true,
			assignmentId,
		});
		const entities: ReportDocumentTemplate = transformApiReportDocumentTemplateToReportDocumentTemplate(response);
		return entities;
	}

	public async getDocumentTemplates(
		typeOfCareId?: number | string,
		offset?: number,
		range?: number,
		forAdditionalAssignment?: boolean,
	): Promise<PaginatedResponse<ReportDocumentTemplate>> {
		const response = await this.get<PaginatedResponse<ApiReportDocumentTemplate>>(
			`assignments/reportdocuments/templates`,
			{
				offset,
				range,
				typeOfCareId,
				forAdditionalAssignment,
			},
		);
		const entities = transformApiReportDocumentTemplatesToReportDocumentTemplates(response.entities);
		return {
			...response,
			entities,
		};
	}

	public async createReportDocument(
		reportDocumentTemplateId: string | number,
		assignmentId: string | number,
	): Promise<number> {
		const res = await this.post<
			{ documentId: number },
			{
				reportDocumentTemplateId: string | number;
			}
		>(`assignments/${assignmentId}/reportdocuments`, {
			reportDocumentTemplateId,
		});
		return res.documentId;
	}

	public async updateReportDocument(data: ReportDocumentUpdateBody): Promise<void> {
		const body = transformReportUpdateBodyToApiReportUpdateBody(data);
		await this.put<void, ApiReportDocumentUpdateBody>(
			`assignments/${data.assignmentId}/reportDocuments/${data.reportId}`,
			body,
		);
	}

	public async getDocumentsOfAssignment(
		assignmentId: string | number,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<ReportDocument>> {
		const response = await this.get<PaginatedResponse<ApiReportDocument>>(
			`assignments/${assignmentId}/reportdocuments`,
			{
				offset,
				range,
			},
		);
		const entities = response.entities.map(transformApiReportDocumentToReportDocument);
		return {
			...response,
			entities,
		};
	}

	public async getReportOfAssignment(
		assignmentId: string | number,
		reportId: string | number,
	): Promise<ReportDocument> {
		const res = await this.get<ApiReportDocument>(`assignments/${assignmentId}/reportdocuments/${reportId}`);
		return transformApiReportDocumentToReportDocument(res);
	}

	public async getPdfOfReportDocument(assignmentId: string | number, reportId: string | number) {
		return this.get<Blob>(
			`uploads/assignments/${assignmentId}/reports/${reportId}`,
			{},
			{
				responseType: 'blob' as 'json',
			},
		);
	}

	public async updateReportTemplate(templateId: string | number, htmlString: string): Promise<void> {
		await this.post<void, { htmlContent: string }>(`templates/${templateId}`, {
			htmlContent: htmlString,
		});
	}
}

@Injectable()
export class MockReportApi implements ReportProvider {
	public async prefillTemplateForAssignment(
		_assignmentId: string | number,
		templateId: string | number,
	): Promise<ReportDocumentTemplate> {
		return mockReportDocumentTemplates.find((doc) => doc.id === Number(templateId))!;
	}

	public async getDocumentTemplates(
		_typeOfCareId?: number | string,
		offset?: number,
		range?: number,
		_forAdditionalAssignment?: boolean,
	): Promise<PaginatedResponse<ReportDocumentTemplate>> {
		const response: PaginatedResponse<ReportDocumentTemplate> = {
			entities: mockReportDocumentTemplates,
			totalEntities: mockReportDocumentTemplates.length,
			offset: offset ?? 0,
			range: range ?? mockReportDocumentTemplates.length,
		};
		return response;
	}

	public async createReportDocument(
		_reportDocumentTemplateId: string | number,
		_assignmentId: string | number,
	): Promise<number> {
		return 1;
	}

	public async updateReportDocument(_data: ReportDocumentUpdateBody): Promise<void> {
		// Mock implementation does nothing
	}

	public async getDocumentsOfAssignment(
		_assignmentId: string | number,
		_offset?: number,
		_range?: number,
	): Promise<PaginatedResponse<ReportDocument>> {
		return {
			entities: mockReportDocuments,
			totalEntities: mockReportDocuments.length,
			offset: 0,
			range: mockReportDocuments.length,
		};
	}

	public async getReportOfAssignment(
		_assignmentId: string | number,
		_reportId: string | number,
	): Promise<ReportDocument> {
		return mockReportDocuments.find((doc) => doc.id === Number(_reportId))!;
	}

	public async getPdfOfReportDocument(_assignmentId: string | number, _reportId: string | number): Promise<Blob> {
		const blob = new Blob([`Mock PDF content for report ${_reportId}`], { type: 'application/pdf' });
		return blob;
	}

	public async updateReportTemplate(_templateId: string | number, _htmlString: string): Promise<void> {
		// Mock implementation does nothing
	}
}

export const provideReportApi: Provider = {
	provide: REPORT_API,
	useClass: ReportApi,
};
